import React, { useCallback, useEffect, useState } from "react";
import { FaEdit, FaTrash, FaCalendarAlt, FaPlus, FaPhone, FaChevronLeft, FaChevronRight, FaStar, FaCheckCircle, FaInfoCircle, FaTimesCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import BottomNav from "../common/BottomNav";
import AppHeader from "../common/AppHeader";
import Spinner from "../common/Spinner";
import DeleteConfirmModal from "../common/DeleteConfirmModal";
import InfoPopup from "../common/InfoPopup";
import CategoryIcon from "../common/CategoryIcon";
import { getEventServicesBySpIdApi, deleteEventServiceApi } from "../../services/eventService";
import { getServiceLabel, getServiceIcon } from "../utils/serviceCategories";
import { useTranslation } from "react-i18next";
import styles from "../css/ManageListingServices.module.css";

export default function ManageEventServices() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [eventServices, setEventServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slideIndexes, setSlideIndexes] = useState({});
  const [deleteModal, setDeleteModal] = useState({ open: false, service: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [descriptionModal, setDescriptionModal] = useState({ open: false, title: "", description: "" });

  const normalizeServiceEntry = useCallback((item) => {
    const baseService =
      item?.listingService && typeof item.listingService === "object"
        ? item.listingService
        : item;

    if (!baseService || typeof baseService !== "object") {
      return null;
    }

    const ratings = Array.isArray(item?.ratings)
      ? item.ratings
      : Array.isArray(baseService?.ratings)
        ? baseService.ratings
        : [];

    const numericRatings = ratings
      .map((r) => Number(r?.ratingValue))
      .filter((v) => Number.isFinite(v) && v > 0);

    const ratingCount = numericRatings.length;
    const averageRating =
      ratingCount > 0
        ? numericRatings.reduce((sum, value) => sum + value, 0) / ratingCount
        : 0;

    return {
      ...baseService,
      ratings,
      ratingCount,
      averageRating,
    };
  }, []);

  const fetchEventServices = useCallback(async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user.spId || "";
      const res = await getEventServicesBySpIdApi(userId);
      const rawData = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.responseData)
          ? res.data.responseData
          : Array.isArray(res?.responseData)
            ? res.responseData
            : [];

      let data = rawData
        .flat()
        .map(normalizeServiceEntry)
        .filter((item) => item && item.esId);

      data = data.slice().sort((a, b) => {
        const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return bTime - aTime;
      });
      setEventServices(data);
    } catch {
      setEventServices([]);
    }
    setLoading(false);
  }, [normalizeServiceEntry]);

  useEffect(() => {
    fetchEventServices();
  }, [fetchEventServices]);

  const handlePrev = (esId, imagesLength) => {
    setSlideIndexes((prev) => ({
      ...prev,
      [esId]: prev[esId] > 0 ? prev[esId] - 1 : imagesLength - 1
    }));
  };

  const handleNext = (esId, imagesLength) => {
    setSlideIndexes((prev) => ({
      ...prev,
      [esId]: prev[esId] < imagesLength - 1 ? prev[esId] + 1 : 0
    }));
  };

  const handleDelete = (service) => {
    setDeleteModal({ open: true, service });
    setDeleteError("");
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    setDeleteError("");
    try {
      const res = await deleteEventServiceApi(deleteModal.service.esId);
      if (res?.success) {
        setEventServices(eventServices.filter(s => s.esId !== deleteModal.service.esId));
        setDeleteModal({ open: false, service: null });
      } else {
        setDeleteError(res?.responseMessage || "Failed to delete service.");
      }
    } catch {
      setDeleteError("Failed to delete service.");
    }
    setDeleteLoading(false);
  };

  const showDescriptionModal = (businessName, description) => {
    setDescriptionModal({ open: true, title: businessName, description });
  };

  const getServiceAdminComment = (svc) => {
    const keys = [
      "comment",
      "adminComment",
      "rejectionReason",
      "rejectReason",
      "deactivationReason",
      "deactivateReason",
      "reason",
    ];
    for (const key of keys) {
      const val = svc?.[key];
      if (typeof val === "string" && val.trim()) return val.trim();
    }
    return "";
  };

  // Calculate summary statistics
  const totalServices = eventServices.length;
  const uniqueCategories = new Set(
    eventServices
      .flatMap(s => s.subCategory ? s.subCategory.split(",").map(c => c.trim()) : [])
      .filter(Boolean)
  );
  const totalCategories = uniqueCategories.size;

  const headerRightContent = (
    <button
      className={styles.addButton}
      title={t("ManageEventServices.manage_event_services")}
      onClick={() => navigate("/add-event-service")}
    >
      <FaPlus size={18} color="#232b35" />
    </button>
  );

  const renderAverageStars = (average) => {
    const filledStars = Math.round(Number(average) || 0);
    return Array.from({ length: 5 }, (_, idx) => (
      <FaStar
        key={idx}
        className={idx < filledStars ? styles.ratingStarFilled : styles.ratingStarEmpty}
      />
    ));
  };

  return (
    <div className={styles.pageContainer}>
      <AppHeader
        title={t("ManageEventServices.manage_listings", "Manage Listings")}
        showBack={true}
        rightContent={headerRightContent}
      />
      <div className={styles.contentWrapper}>
        {/* Summary Info Card */}
        {!loading && eventServices.length > 0 && (
          <div className={styles.summaryInfoCard}>
            <div className={styles.summaryInfoHeader}>
              <FaStar size={20} />
              <span>{t("ManageEventServices.your_services", "Your Services")}</span>
            </div>
            <div className={styles.summaryInfoGrid}>
              <div className={styles.summaryInfoItem}>
                <div className={styles.summaryInfoValue}>{totalServices}</div>
                <div className={styles.summaryInfoLabel}>{t("ManageEventServices.total_services", "Total Services")}</div>
              </div>
              <div className={styles.summaryInfoItem}>
                <div className={styles.summaryInfoValue}>{totalCategories}</div>
                <div className={styles.summaryInfoLabel}>{t("ManageEventServices.categories", "Categories")}</div>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className={styles.loadingContainer}>
            <Spinner text={t("Common.loading")} />
          </div>
        ) : eventServices.length === 0 ? (
          <div className={styles.emptyContainer}>
            <div className={styles.emptyIcon}>
              <FaCalendarAlt size={38} color="#ec4899" />
            </div>
            <div className={styles.emptyTitle}>
              {t("ManageEventServices.no_services_yet", "No Services Yet")}
            </div>
            <div className={styles.emptyMessage}>
              {t("ManageEventServices.add_first_service", "Add your first service listing to get started")}
            </div>
          </div>
        ) : eventServices.map((serviceItem) => {
          const service = normalizeServiceEntry(serviceItem) || serviceItem;
          const images = service.serviceImageUrls
            ? service.serviceImageUrls.split(",").map(url => url.trim()).filter(Boolean)
            : [];
          const currentIdx = slideIndexes[service.esId] || 0;
          const priceDisplay = service.maxPrice && service.maxPrice !== service.minPrice
            ? `₹${service.minPrice} - ₹${service.maxPrice}`
            : `₹${service.minPrice}+`;
          
          return (
            <div key={service.esId} className={styles.roadlightCard}>
              <div className={styles.imageContainer}>
                {images.length > 0 ? (
                  <>
                    <img
                      src={images[currentIdx]}
                      alt={service.businessName}
                      className={styles.roadlightImage}
                    />
                    {images.length > 1 && (
                      <>
                        <button
                          className={styles.slideButtonLeft}
                          onClick={() => handlePrev(service.esId, images.length)}
                          title="Previous"
                        >
                          <FaChevronLeft />
                        </button>
                        <button
                          className={styles.slideButtonRight}
                          onClick={() => handleNext(service.esId, images.length)}
                          title="Next"
                        >
                          <FaChevronRight />
                        </button>
                        <div className={styles.slideDots}>
                          {images.map((_, idx) => (
                            <span
                              key={idx}
                              className={`${styles.dot} ${idx === currentIdx ? styles.dotActive : ''}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <FaCalendarAlt size={80} color="#ec4899" />
                )}
                {/* Approved Badge Overlay */}
                {(service.approvalStatus?.toLowerCase() === 'approved') && (
                  <div className={styles.approvedBadge}>
                    <FaCheckCircle size={14} />
                    <span>Approved</span>
                  </div>
                )}
                {/* Rejected Badge Overlay */}
                {(service.approvalStatus?.toLowerCase() === 'rejected') && (
                  <div className={styles.rejectedBadge}>
                    <FaTimesCircle size={14} />
                    <span>Rejected</span>
                  </div>
                )}
                {/* Multiple category badges */}
                <div style={{ position: 'absolute', bottom: '8px', left: '8px', right: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {(service.subCategory ? service.subCategory.split(",").map(c => c.trim()) : []).map((cat, idx) => {
                    const icon = getServiceIcon(cat);
                    return (
                      <span key={idx} className={styles.typeBadge}>
                        <CategoryIcon icon={icon} size={16} style={{ marginRight: '4px' }} />
                        {getServiceLabel(cat)}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.roadlightName} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span>{service.businessName}</span>
                  {(service.approvalStatus?.toLowerCase() === 'approved') && (
                    <span className={styles.verifiedBadge}>
                      <FaCheckCircle size={10} />
                      Verified
                    </span>
                  )}
                </div>
                <div className={styles.ownerName}>
                  {service.ownerName}
                </div>
                <div className={styles.ratingRow}>
                  {service.ratingCount > 0 ? (
                    <>
                      <span className={styles.ratingStars}>
                        {renderAverageStars(service.averageRating)}
                      </span>
                      <span className={styles.ratingValue}>{service.averageRating.toFixed(1)}</span>
                      <span className={styles.ratingCount}>({service.ratingCount})</span>
                    </>
                  ) : (
                    <span className={styles.ratingEmpty}>{t("ManageEventServices.no_ratings", "No ratings yet")}</span>
                  )}
                </div>

                <div className={styles.contactInfo}>
                  <FaPhone className={styles.phoneIcon} /> {service.primaryContact || service.contactNumber}
                  {service.whatsappNumber && (
                    <span style={{ marginLeft: '8px', color: '#25D366', fontWeight: '500' }} title="WhatsApp Number">
                      • 📱 {service.whatsappNumber}
                    </span>
                  )}
                </div>
                {service.serviceDescription && (
                  <div 
                    className={styles.infoText} 
                    onClick={() => showDescriptionModal(service.businessName, service.serviceDescription)}
                    style={{ 
                      marginBottom: '8px', 
                      fontStyle: 'italic', 
                      color: '#667eea', 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    title="Click to view full description"
                  >
                    <FaInfoCircle size={14} />
                    {service.serviceDescription.substring(0, 80)}
                    {service.serviceDescription.length > 80 ? '...' : ''}
                  </div>
                )}
                <div className={styles.infoText}>
                  {t("ManageEventServices.address")}: {service.address}, {service.city}, {service.state}
                </div>
                <div className={styles.infoText}>
                  {t("ManageEventServices.price_range")}: {priceDisplay}
                  {service.experience && service.experience > 0 && (
                    <> | {t("ManageEventServices.experience")}: {service.experience} {t("ManageEventServices.years")}</>
                  )}
                </div>
                <div className={styles.infoText}>
                  Service ID: {service.esId}
                </div>
                <div className={styles.infoTextSmall}>
                  {t("ManageEventServices.registered_on", "Registered")}: {service.createdAt ? new Date(service.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : "N/A"}
                  {service.updatedAt && service.updatedAt !== service.createdAt && (
                    <> | {t("ManageEventServices.updated_on", "Updated")}: {new Date(service.updatedAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</>
                  )}
                </div>
                <div className={styles.infoTextSmall}>
                  {t("ManageEventServices.status")}: {service.status} | {t("ManageEventServices.approval")}: {service.approvalStatus}
                </div>
                {(String(service.status ?? "").toLowerCase() === "inactive" ||
                  String(service.approvalStatus ?? "").toLowerCase() === "rejected") &&
                  getServiceAdminComment(service) && (
                    <div className={styles.adminNotice}>
                      <span className={styles.adminNoticeLabel}>{t("ManageEventServices.admin_comment", "Admin comment")}: </span>
                      <span>{getServiceAdminComment(service)}</span>
                    </div>
                )}
                <div className={styles.actionButtons}>
                  <button
                    className={styles.viewButton}
                    title={t("ManageEventServices.view_service", "View Service")}
                    onClick={() => navigate(`/event-service/${service.esId}`, { state: { service } })}
                  >
                    {t("ManageEventServices.view_service", "View")}
                  </button>
                  <button
                    className={styles.editButton}
                    title={t("ManageEventServices.edit_service")}
                    onClick={() => navigate(`/edit-event-service/${service.esId}`, { state: { service } })}
                  >
                    <FaEdit className={styles.buttonIcon} />
                    {t("Common.edit")}
                  </button>
                  <button
                    className={styles.deleteButton}
                    title={t("ManageEventServices.delete_service")}
                    onClick={() => handleDelete(service)}
                    disabled={deleteLoading}
                  >
                    <FaTrash className={styles.buttonIcon} />
                    {deleteLoading && deleteModal.service?.esId === service.esId
                      ? t("ManageEventServices.deleting")
                      : t("Common.delete")}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <DeleteConfirmModal
        open={deleteModal.open}
        title={t("ManageEventServices.confirm_delete")}
        message={
          <>
            {t("ManageEventServices.confirm_delete_message")}
          </>
        }
        error={deleteError}
        loading={deleteLoading}
        onCancel={() => setDeleteModal({ open: false, service: null })}
        onConfirm={confirmDelete}
      />
      <InfoPopup
        show={descriptionModal.open}
        status="success"
        title={descriptionModal.title}
        message={descriptionModal.description}
        onClose={() => setDescriptionModal({ open: false, title: "", description: "" })}
      />
      <BottomNav />
    </div>
  );
}
